import React, { useCallback, useEffect, useState } from 'react';
import { plansAPI } from '../../../services/api';

const emptyForm = {
  slug: '',
  name: '',
  price: 0,
  minutes: 0,
  description: '',
  features: '',
  product_id: '',
  is_active: true,
  is_purchasable: true,
  is_popular: false,
  sort_order: 0,
};

// Features come back from the API as either a JSON array (parsed by mysql2)
// or a JSON string depending on the driver path. Normalise to a newline list
// for the textarea, and back to an array on submit.
function featuresToText(value) {
  if (!value) return '';
  if (Array.isArray(value)) return value.join('\n');
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.join('\n');
    } catch { /* fall through */ }
    return value;
  }
  return '';
}

const AdminPlansTab = () => {
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // existing plan
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await plansAPI.listAll();
      setPlans(res.data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const startCreate = () => {
    setForm(emptyForm);
    setEditing(null);
    setCreating(true);
  };

  const startEdit = (plan) => {
    setForm({
      slug: plan.slug,
      name: plan.name,
      price: plan.price,
      minutes: plan.minutes,
      description: plan.description || '',
      features: featuresToText(plan.features),
      product_id: plan.product_id || '',
      is_active: !!plan.is_active,
      is_purchasable: !!plan.is_purchasable,
      is_popular: !!plan.is_popular,
      sort_order: plan.sort_order || 0,
    });
    setEditing(plan);
    setCreating(false);
  };

  const closeForm = () => {
    setEditing(null);
    setCreating(false);
    setForm(emptyForm);
  };

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    // Send features as a string — the API splits on newlines server-side.
    const body = {
      ...form,
      price: Number(form.price) || 0,
      minutes: Number(form.minutes) || 0,
      sort_order: Number(form.sort_order) || 0,
      features: form.features,
    };
    try {
      if (editing) {
        await plansAPI.update(editing.id, body);
      } else {
        await plansAPI.create(body);
      }
      closeForm();
      await reload();
    } catch (e) {
      alert(e.message);
    }
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`Delete plan "${plan.name}"? Users on this plan will lose their plan link.`)) return;
    try {
      await plansAPI.remove(plan.id);
      await reload();
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <div className="admin-section">
      <div className="admin-section-head">
        <h2>Plans</h2>
        <button className="primary" onClick={startCreate}>+ New plan</button>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {loading ? (
        <div className="admin-empty">Loading…</div>
      ) : plans.length === 0 ? (
        <div className="admin-empty">No plans yet.</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Slug</th>
                <th>Name</th>
                <th>Price (TRY)</th>
                <th>Minutes</th>
                <th>Active</th>
                <th>Purchasable</th>
                <th>Popular</th>
                <th>Order</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id}>
                  <td>{p.id}</td>
                  <td><code>{p.slug}</code></td>
                  <td>{p.name}</td>
                  <td>{Number(p.price).toFixed(2)}</td>
                  <td>{p.minutes}</td>
                  <td>{p.is_active ? '✓' : '—'}</td>
                  <td>{p.is_purchasable ? '✓' : '—'}</td>
                  <td>{p.is_popular ? '★' : '—'}</td>
                  <td>{p.sort_order}</td>
                  <td className="admin-actions">
                    <button onClick={() => startEdit(p)}>Edit</button>
                    <button className="danger" onClick={() => handleDelete(p)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(editing || creating) && (
        <div className="admin-modal-backdrop" onClick={closeForm}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editing ? `Edit "${editing.slug}"` : 'New plan'}</h3>
            <label>Slug (unique, lowercase)</label>
            <input name="slug" value={form.slug} onChange={handleChange} disabled={!!editing} />
            <label>Name</label>
            <input name="name" value={form.name} onChange={handleChange} />
            <label>Price (TRY)</label>
            <input type="number" step="0.01" name="price" value={form.price} onChange={handleChange} />
            <label>Minutes</label>
            <input type="number" name="minutes" value={form.minutes} onChange={handleChange} />
            <label>Description</label>
            <textarea name="description" value={form.description} onChange={handleChange} rows={2} />
            <label>Features (one per line — shown as bullets on the pricing card)</label>
            <textarea name="features" value={form.features} onChange={handleChange} rows={5}
                      placeholder={'500 dakika transkripsiyon\nTüm dosya formatları destekli\n...'} />
            <label>Iyzico product ID</label>
            <input name="product_id" value={form.product_id} onChange={handleChange} />
            <label>Sort order</label>
            <input type="number" name="sort_order" value={form.sort_order} onChange={handleChange} />
            <label className="admin-check">
              <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange} />
              Active (shown on the public list)
            </label>
            <label className="admin-check">
              <input type="checkbox" name="is_purchasable" checked={form.is_purchasable} onChange={handleChange} />
              Purchasable (can be bought via Iyzico)
            </label>
            <label className="admin-check">
              <input type="checkbox" name="is_popular" checked={form.is_popular} onChange={handleChange} />
              Popular (highlighted "Most popular" card)
            </label>
            <div className="admin-modal-actions">
              <button onClick={closeForm}>Cancel</button>
              <button onClick={handleSubmit} className="primary">{editing ? 'Save' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPlansTab;
